import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Upload, Trash2, Image as ImageIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { AiIntegration } from "./AiSettings";
import { AddressAutocomplete } from "../../components/AddressAutocomplete";
import { COUNTRIES, CURRENCIES, currencyForCountry } from "../../lib/locale";

const INVENTORY_TYPE_OPTIONS = [["general", "General"], ["bar", "Bar / Bottle shop"], ["restaurant", "Restaurant / Cafe"], ["warehouse", "Warehouse / Distribution"], ["chemical", "Chemical / Cleaning"], ["construction", "Construction / Trades"]];
import { api, errMsg, fmtDate, download } from "../../lib/api";
import { useAuth, canWrite } from "../../context/AuthContext";
import nxApi from "../../nestix/services/api";

function CompanyLogo() {
  const { session } = useAuth();
  const [logo, setLogo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { nxApi.get("/settings").then((r) => setLogo(r.data?.company_logo_url || "")).catch(() => {}); }, []);
  const changed = () => window.dispatchEvent(new Event("company-settings-updated"));
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) { return; }
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(file.type)) { toast.error("Please choose a PNG, JPG, SVG or WebP image"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Logo must be smaller than 5 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await nxApi.post("/settings/upload-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(r.data.logo_url);
      changed();
      toast.success("Logo updated — it now shows on your POs, invoices, quotes and other printed forms");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  const remove = async () => {
    try { await nxApi.delete("/settings/logo"); setLogo(""); changed(); toast.success("Logo removed"); } catch (e2) { toast.error(errMsg(e2)); }
  };
  const w = canWrite(session);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl mb-6" data-testid="company-logo-card">
      <h2 className="font-semibold text-slate-900">Company logo</h2>
      <p className="text-sm text-slate-500 mt-1">Used on purchase orders, invoices, quotes, stocktake sheets and other printed forms.</p>
      <div className="mt-4 flex flex-wrap items-center gap-6">
        <div className="h-24 w-48 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center bg-slate-50 p-2">
          {logo ? <img src={logo} alt="Company logo" className="max-h-full max-w-full object-contain" data-testid="company-logo-preview" /> : <ImageIcon className="w-8 h-8 text-slate-300" />}
        </div>
        {w && (
          <div className="flex flex-wrap gap-2">
            <label className="bg-sky-600 hover:bg-sky-700 transition-colors text-white font-semibold px-5 py-2.5 rounded-md text-sm cursor-pointer inline-flex items-center gap-2" data-testid="company-logo-upload-btn">
              <Upload className="w-4 h-4" />{busy ? "Uploading…" : logo ? "Change logo" : "Upload logo"}
              <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" className="hidden" onChange={upload} disabled={busy} data-testid="company-logo-input" />
            </label>
            {logo && <button type="button" onClick={remove} className="border border-red-200 text-red-700 hover:bg-red-50 transition-colors font-semibold px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2" data-testid="company-logo-remove-btn"><Trash2 className="w-4 h-4" />Remove</button>}
          </div>
        )}
      </div>
      <p className="text-xs text-slate-400 mt-3">PNG, JPG, SVG or WebP, max 5 MB. A wide logo on a white or transparent background prints best.</p>
    </div>
  );
}

function OrgDetails() {
  const { session, refresh } = useAuth();
  const o = session.org;
  const [f, setF] = useState({ name: o.name, phone: o.phone || "", address: o.address || "", abn: o.abn || "", contact_email: o.contact_email || "", country: o.country || "AU", currency: o.currency || "AUD", tax_name: o.tax_name || "GST", tax_rate: o.tax_rate ?? 10, inventory_type: o.inventory_type || "general", po_prices_include_tax: o.po_prices_include_tax ?? true });
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/org", { ...f, tax_rate: f.tax_rate === "" ? 0 : Number(f.tax_rate) });
      toast.success("Organisation details saved");
      refresh();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  const w = canWrite(session);
  return (
    <form onSubmit={save} className="bg-white border border-slate-200 rounded-xl p-6 grid sm:grid-cols-2 gap-4 max-w-3xl" data-testid="org-details-form">
      {[["name", "Organisation name"], ["contact_email", "Contact email"], ["phone", "Phone"], ["abn", "Business / tax number (e.g. ABN, VAT, EIN)"]].map(([k, l]) => (
        <label key={k} className={`block text-sm font-medium ${k === "address" ? "sm:col-span-2" : ""}`}>{l}<input disabled={!w} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="field mt-1.5" data-testid={`org-${k}-input`} /></label>
      ))}
      <label className="block text-sm font-medium">Country
        <select disabled={!w} value={f.country} onChange={(e) => { const c = e.target.value; setF((p) => ({ ...p, country: c, currency: currencyForCountry(c) || p.currency })); }} className="field mt-1.5" data-testid="org-country-select">
          {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>
      <label className="block text-sm font-medium">Home currency
        <select disabled={!w} value={f.currency} onChange={(e) => setF((p) => ({ ...p, currency: e.target.value }))} className="field mt-1.5" data-testid="org-currency-select">
          {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
        </select>
      </label>
      <label className="block text-sm font-medium">Tax name
        <input disabled={!w} value={f.tax_name} onChange={(e) => setF((p) => ({ ...p, tax_name: e.target.value }))} placeholder="GST, VAT, Sales tax" className="field mt-1.5" data-testid="org-tax-name-input" />
      </label>
      <label className="block text-sm font-medium">Tax rate %
        <input disabled={!w} type="number" min="0" max="100" step="any" value={f.tax_rate} onChange={(e) => setF((p) => ({ ...p, tax_rate: e.target.value }))} className="field mt-1.5" data-testid="org-tax-rate-input" />
      </label>
      <label className="block text-sm font-medium sm:col-span-2">Inventory type
        <select disabled={!w} value={f.inventory_type} onChange={(e) => setF((p) => ({ ...p, inventory_type: e.target.value }))} className="field mt-1.5" data-testid="org-inventory-type-select">
          {INVENTORY_TYPE_OPTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <span className="block text-xs font-normal text-slate-500 mt-1">Decides which fields General Inventory shows for your items.</span>
      </label>
      <label className="flex items-start gap-3 text-sm font-medium sm:col-span-2 border border-slate-200 rounded-md px-3 py-2.5">
        <input type="checkbox" disabled={!w} checked={!!f.po_prices_include_tax} onChange={(e) => setF((p) => ({ ...p, po_prices_include_tax: e.target.checked }))} className="mt-0.5 h-4 w-4" data-testid="org-po-prices-include-tax" />
        <span>Purchase order prices include {f.tax_name || "tax"}<span className="block text-xs font-normal text-slate-500 mt-0.5">When ticked, the prices you enter on purchase orders already include {f.tax_name || "tax"}. The total is not increased; the {f.tax_name || "tax"} inside it is shown separately.</span></span>
      </label>
      <label className="block text-sm font-medium sm:col-span-2">Address
        <div className="mt-1.5"><AddressAutocomplete disabled={!w} value={f.address} onChange={(v) => setF((p) => ({ ...p, address: v }))} onSelect={(a) => setF((p) => ({ ...p, address: a.full }))} className="field" testid="org-address-input" /></div>
      </label>
      <div className="sm:col-span-2 flex justify-between items-center">
        <span className="text-xs text-slate-500 font-mono">Tenant ID: {session.org.tenant_id}</span>
        {w && <button className="bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-md text-sm" data-testid="org-save-btn">Save</button>}
      </div>
    </form>
  );
}

function SupportAccess() {
  const { session, refresh } = useAuth();
  const on = !!session.org.support_access;
  const inSupport = !!session.user.support;
  const toggle = async () => {
    try {
      await api.put("/org/support-access", { enabled: !on });
      toast.success(on ? "Support access turned off" : "Support access turned on");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-3xl mt-6 flex items-start justify-between gap-6" data-testid="support-access-card">
      <div>
        <h3 className="font-semibold">QORDEL support access</h3>
        <p className="text-sm text-slate-600 mt-1">Allow the QORDEL 360 support team to sign in to your organisation to help fix issues. Every visit is recorded in your Audit log. Turn it off at any time.</p>
        <p className="text-sm mt-2 font-semibold" data-testid="support-access-status">{on ? "Allowed" : "Not allowed"}</p>
      </div>
      {session.user.role === "org_admin" && !(inSupport && !on) && <button onClick={toggle} className={`shrink-0 font-semibold px-4 py-2 rounded-md text-sm ${on ? "border border-red-200 text-red-700" : "bg-sky-600 text-white"}`} data-testid="support-access-toggle">{on ? "Turn off" : "Allow access"}</button>}
    </div>
  );
}

function DataPrivacy() {
  const { session, logout } = useAuth();
  const [req, setReq] = useState({ category: "data_export", message: "" });
  const w = canWrite(session);
  const demo = async (method) => {
    try {
      const r = await api[method]("/org/demo-data");
      toast.success(method === "post" ? "Demonstration data loaded" : `Removed ${r.data.deleted} demo records`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support", { subject: req.category === "data_deletion" ? "Account closure & data deletion request" : "Data export request", message: req.message, category: req.category });
      toast.success("Request submitted. We will contact you to confirm.");
      setReq({ ...req, message: "" });
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-semibold">Export your data</h3>
        <p className="text-sm text-slate-600">Download every record, user and organisation detail for your organisation as a JSON file. Individual registers can be exported to CSV from each register.</p>
        <button onClick={() => download("/org/export", `qordel360-export-${session.org.tenant_id}.json`).catch((e) => toast.error(errMsg(e)))} className="inline-flex items-center gap-2 bg-sky-600 text-white font-semibold px-4 py-2.5 rounded-md text-sm" data-testid="org-export-btn"><Download className="w-4 h-4" />Download full export</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-semibold">Demonstration data</h3>
        <p className="text-sm text-slate-600">Demo records are labelled DEMO and kept separate from your real records.</p>
        <div className="flex gap-2">
          <button disabled={!w} onClick={() => demo("post")} className="border border-slate-300 font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-50" data-testid="demo-load-btn">Load demo data</button>
          <button disabled={!w} onClick={() => demo("delete")} className="border border-red-200 text-red-700 font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-50" data-testid="demo-remove-btn">Remove demo data</button>
        </div>
      </div>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-semibold">Request export or deletion</h3>
        <select value={req.category} onChange={(e) => setReq({ ...req, category: e.target.value })} className="field" data-testid="data-request-type"><option value="data_export">Assisted data export (including documents)</option><option value="data_deletion">Close account &amp; delete data</option></select>
        <textarea required minLength={5} rows={3} value={req.message} onChange={(e) => setReq({ ...req, message: e.target.value })} placeholder="Tell us what you need" className="field" data-testid="data-request-message" />
        <button className="bg-slate-900 text-white font-semibold px-4 py-2.5 rounded-md text-sm" data-testid="data-request-submit">Submit request</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-3">
        <h3 className="font-semibold">Sessions</h3>
        <p className="text-sm text-slate-600">Sign out of QORDEL 360 on every device.</p>
        <button onClick={async () => { await api.post("/auth/logout-all"); await logout(); }} className="border border-slate-300 font-semibold px-4 py-2 rounded-md text-sm" data-testid="logout-all-btn">Log out everywhere</button>
      </div>
    </div>
  );
}

function AuditLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/org/audit-logs").then((r) => setRows(r.data));
  }, []);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid="org-audit-table">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">User</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Detail</th></tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} className="border-t border-slate-100"><td className="px-4 py-2 whitespace-nowrap">{new Date(r.ts).toLocaleString("en-AU")}</td><td className="px-4 py-2">{r.user_email || "System"}</td><td className="px-4 py-2 font-mono text-xs">{r.action}</td><td className="px-4 py-2 text-slate-600">{[r.entity, r.detail].filter(Boolean).join(" · ")}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="settings-page">
      <div><p className="eyebrow">Administration</p><h1 className="mt-2 text-3xl font-bold tracking-tight">Settings &amp; Data</h1></div>
      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "org"}>
        <TabsList><TabsTrigger value="org" data-testid="settings-tab-org">Organisation</TabsTrigger><TabsTrigger value="ai" data-testid="settings-tab-ai">AI Integration</TabsTrigger><TabsTrigger value="data" data-testid="settings-tab-data">Data &amp; privacy</TabsTrigger><TabsTrigger value="audit" data-testid="settings-tab-audit">Audit log</TabsTrigger></TabsList>
        <TabsContent value="ai" className="mt-6"><AiIntegration /></TabsContent>
        <TabsContent value="org" className="mt-6"><CompanyLogo /><OrgDetails /><SupportAccess /></TabsContent>
        <TabsContent value="data" className="mt-6"><DataPrivacy /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditLog /></TabsContent>
      </Tabs>
    </div>
  );
}

export function Support() {
  const [f, setF] = useState({ subject: "", message: "", category: "support" });
  const [rows, setRows] = useState([]);
  const load = () => api.get("/support").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/support", f);
      toast.success("Support request submitted");
      setF({ subject: "", message: "", category: "support" });
      load();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="support-page">
      <div><p className="eyebrow">Help</p><h1 className="mt-2 text-3xl font-bold tracking-tight">Support</h1></div>
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-xl p-6 grid gap-4">
        <div className="grid sm:grid-cols-3 gap-4">
          <input required minLength={3} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" className="field sm:col-span-2" data-testid="support-subject-input" />
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="field" data-testid="support-category-select"><option value="support">General support</option><option value="billing">Billing</option></select>
        </div>
        <textarea required minLength={5} rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="How can we help?" className="field" data-testid="support-message-input" />
        <button className="justify-self-start bg-sky-600 text-white font-semibold px-5 py-2.5 rounded-md text-sm" data-testid="support-submit-btn">Submit request</button>
      </form>
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="support-list">
        {rows.map((r) => <div key={r.id} className="p-4"><div className="flex justify-between"><p className="font-semibold">{r.subject}</p><span className="text-xs uppercase font-semibold text-slate-500">{r.status.replace("_", " ")}</span></div><p className="text-sm text-slate-600 mt-1">{r.message}</p>{r.response && <p className="text-sm mt-2 bg-sky-50 p-2 rounded">QORDEL 360: {r.response}</p>}<p className="text-xs text-slate-400 mt-1">{fmtDate(r.created_at)}</p></div>)}
        {rows.length === 0 && <p className="p-6 text-sm text-slate-500">No requests yet.</p>}
      </div>
    </div>
  );
}
